import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Download } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Features',  to: '/features' },
  { label: 'Pricing',   to: '/pricing' },
  { label: 'Use Cases', to: '/use-cases' },
  { label: 'Docs',      to: '/documentation' },
  { label: 'Contact',   to: '/contact' },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header
      // The app's topbar carries a soft shadow, not a bottom rule.
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-200 ${
        scrolled ? 'surface-blur shadow-soft' : 'bg-surface'
      }`}
    >
      <div className="container-page">
        <div className="flex h-[72px] items-center justify-between gap-4">

          <Link to="/" className="focus-ring flex-shrink-0 rounded-sm" aria-label="WASLX home">
            <img src="/logo-loop.webp" alt="WASLX" className="logo-anim h-11 w-auto" />
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map(link => {
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`focus-ring rounded-md px-3 py-2 text-[0.9375rem] transition-colors duration-150 ${
                    active
                      ? 'bg-primary-tint text-primary-strong'
                      : 'text-content-muted hover:bg-surface-muted hover:text-content'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <a href="/waslx.apk" download className="btn btn-ghost btn--sm">
              <Download className="h-4 w-4" />
              Download App
            </a>
            {/* The one leading action on the page — .btn-ink, never two. */}
            <Link to="/contact" className="btn-pill btn-pill--primary btn-pill--sm">
              Book a Demo
            </Link>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md text-content-muted hover:bg-surface-muted hover:text-content"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-line bg-surface md:hidden">
          <nav className="container-page space-y-1 py-3">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`block rounded-md px-3 py-2.5 text-[0.9375rem] transition-colors ${
                  location.pathname === link.to
                    ? 'bg-primary-tint text-primary-strong'
                    : 'text-content-muted hover:bg-surface-muted hover:text-content'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="grid gap-2 pt-3">
              <a href="/waslx.apk" download className="btn-pill btn-pill--block">
                <Download className="h-4 w-4" />
                Download App
              </a>
              <Link to="/contact" className="btn-pill btn-pill--primary btn-pill--block">
                Book a Demo
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
